import { type FC } from "react"
import styled, { css } from "styled-components"
import tinycolor from "tinycolor2"
import Values from "values.js"

import { parseIntNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { useVideos } from "~/api/video"
import useQueryParam from "~/hooks/useQueryParam"
import Button from "~/themes/original/components/Button"
import Card, { Cards } from "~/themes/original/components/Card"
import Layout from "~/themes/original/components/Layout"
import PartialLoadingPage from "~/themes/original/components/PartialLoadingPage"
import routes from "~/utilities/routes"

const VideosPage: FC = () => {
	const videos = useVideos()
	const selectedTag = useQueryParam("tag")

	const selectedTagId = parseIntNumber(selectedTag.value)

	return (
		<Layout>
			{videos.status === "error" ? (
				<p>Failed to load videos.</p>
			) : videos.status === "loading" ? (
				<PartialLoadingPage />
			) : videos.status === "success" ? (
				<>
					{videos.tags.length > 0 && (
						<Tags>
							{videos.tags.map(tag => (
								<Tag
									key={tag.id}
									$isSelected={selectedTagId === tag.id}
									onClick={() => selectedTag.set(tag.id.toString())}
								>
									{tag.label}
								</Tag>
							))}
							{selectedTag.value !== null && (
								<Tag $isSelected onClick={() => selectedTag.set(null)}>
									Clear filter
								</Tag>
							)}
						</Tags>
					)}
					<Cards>
						{(selectedTagId !== null
							? videos.data.filter(video => video.tagIds.includes(selectedTagId))
							: videos.data
						).map(video => (
							<Card
								key={video.id}
								title={video.name}
								description={video.shortDescription ?? undefined}
								thumbnailFilePath={video.thumbnailUrl}
								sticker={{ text: video.priceLabel.value, color: video.priceLabel.color }}
								onClick={routes.video.detail(video.id)}
							/>
						))}
					</Cards>
				</>
			) : (
				exhaustiveGuard(videos.status)
			)}
		</Layout>
	)
}

const Tags = styled.div`
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
	margin-bottom: 16px;

	@media (max-width: 999px) {
		gap: 8px;
		margin-left: 16px;
		margin-right: 16px;
	}
`

const Tag = styled(Button)<{ $isSelected: boolean }>(props => {
	const color = props.$isSelected
		? props.theme.primaryColor
		: new Values(props.theme.primaryColor).tint(50).hexString()

	return css`
		padding: 12px 24px;
		border-radius: 40px;
		background-color: ${color};
		color: ${tinycolor(color).isDark() ? "white" : "black"};
		line-height: 1;
	`
})

export default VideosPage
